import { useState } from 'react';
import { Link, createFileRoute, useRouter } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronLeft } from 'lucide-react';
import {
  Badge,
  DataTable,
  EmptyState,
  PageBody,
  backLinkVariants,
  type DataTableColumn,
} from '@wundo/ui';
import { PageHeader } from '@/components/PageHeader.tsx';
import { RUN_STATUS_TONE, runDuration } from '@/components/insights/runStatus.ts';
import { fetchRoutine, fetchRuns, type InsightRunSummary } from '@/lib/insights.ts';
import { PAGE_SIZE } from '@/lib/pageSize.ts';

export const Route = createFileRoute('/_app/insights/routines/$id/runs')({
  staticData: { orgScope: 'none' },
  component: RunsRoute,
});

function RunsRoute() {
  const { id } = Route.useParams();

  return <InsightRoutineRunsPage routineId={id} />;
}

const columns: DataTableColumn<InsightRunSummary>[] = [
  {
    key: 'started_at',
    header: 'Started',
    nowrap: true,
    render: (run) => (run.started_at ? new Date(run.started_at).toLocaleString() : '—'),
  },
  {
    key: 'status',
    header: 'Status',
    nowrap: true,
    render: (run) => <Badge tone={RUN_STATUS_TONE[run.status]}>{run.status}</Badge>,
  },
  {
    key: 'duration',
    header: 'Duration',
    hideBelow: 'mobile',
    nowrap: true,
    render: (run) => runDuration(run.started_at, run.finished_at) ?? '—',
  },
  {
    key: 'cost_usd',
    header: 'Cost',
    hideBelow: 'mobile',
    nowrap: true,
    render: (run) => `$${(run.cost_usd ?? 0).toFixed(4)}`,
  },
];

export interface InsightRoutineRunsPageProps {
  routineId: string;
}

/** What one routine has done: every iteration, newest first. */
export function InsightRoutineRunsPage({ routineId }: InsightRoutineRunsPageProps) {
  const router = useRouter();
  const [page, setPage] = useState(1);

  const routine = useQuery({
    queryKey: ['insightRoutine', routineId],
    queryFn: () => fetchRoutine(routineId),
  });

  const runs = useQuery({
    queryKey: ['insightRuns', routineId, page],
    queryFn: () => fetchRuns(routineId, page),
    placeholderData: keepPreviousData,
  });

  const rows = runs.data?.data ?? [];
  const total = runs.data?.meta.totalItems ?? 0;

  return (
    <>
      <Link to='/insights' className={backLinkVariants({ className: 'mb-2.5' })}>
        <ChevronLeft aria-hidden='true' />
        Data insights
      </Link>
      <PageHeader title={routine.data ? `Runs · ${routine.data.name}` : 'Runs'} />
      <PageBody className='mt-3.5'>
        <DataTable
          label='Runs'
          columns={columns}
          rows={rows}
          rowKey={(run) => run.id}
          loading={runs.isLoading}
          error={runs.isError || routine.isError ? 'Failed to load runs' : undefined}
          pagination={{ page, pageSize: PAGE_SIZE, total }}
          onPageChange={setPage}
          rowHref={(run) => `/insights/runs/${run.id}`}
          onNavigate={(href) => void router.navigate({ href })}
          emptyState={
            <EmptyState
              title='No runs yet'
              description='This routine has not run. Start one from the routines list.'
            />
          }
        />
      </PageBody>
    </>
  );
}
